import dayjs from 'dayjs'

export type AppTimeInput = number | string | null | undefined

export type AppTimeType = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type RelativeTimeOptions = {
  now?: AppTimeInput
  translate?: (key: string, values?: unknown[]) => string
}

const CHINESE_LOCALES = new Set(['zh-cn', 'zh', 'zh-hk', 'zh-tw'])

const normalizeTimestamp = (timestamp: AppTimeInput) => {
  if (!timestamp) return null

  const rawTimestamp = String(timestamp)
  let normalizedTimestamp = Number(timestamp)

  if (!Number.isFinite(normalizedTimestamp)) {
    normalizedTimestamp = Date.parse(rawTimestamp)
  } else if (rawTimestamp.length <= 10) {
    normalizedTimestamp *= 1000
  }

  return Number.isFinite(normalizedTimestamp) ? normalizedTimestamp : null
}

const isChineseLocale = (languageCode = '') => CHINESE_LOCALES.has(languageCode.toLowerCase())

/**
 * 全局相对时间展示，对齐 App 发现页列表 v2 规则。
 */
export const formatRelativeTime = (timestamp: AppTimeInput, languageCode = '', options: RelativeTimeOptions = {}) => {
  const normalizedTimestamp = normalizeTimestamp(timestamp)
  const normalizedNow = normalizeTimestamp(options.now) ?? Date.now()

  if (!normalizedTimestamp) return ''

  const targetTime = dayjs(normalizedTimestamp)
  const now = dayjs(normalizedNow)
  const diffMs = Math.max(normalizedNow - normalizedTimestamp, 0)
  const minuteMs = 60 * 1000
  const hourMs = 60 * minuteMs
  const dayMs = 24 * hourMs
  const isChinese = isChineseLocale(languageCode)
  const translate = (key: string, values: unknown[], fallback: string) => {
    const translatedText = options.translate?.(key, values)

    return translatedText && translatedText !== key ? translatedText : fallback
  }

  if (diffMs <= 10 * minuteMs) {
    return options.translate?.('84728', []) || ''
  }
  if (diffMs < hourMs) {
    const minutes = Math.max(1, Math.floor(diffMs / minuteMs))

    return translate('84723', [minutes], isChinese ? `${minutes}分钟前` : `${minutes}m`)
  }
  if (diffMs < dayMs) {
    const hours = Math.max(1, Math.floor(diffMs / hourMs))

    return translate('84724', [hours], isChinese ? `${hours}小时前` : `${hours}h`)
  }

  const dayDiff = now.startOf('day').diff(targetTime.startOf('day'), 'day')

  if (dayDiff === 1) {
    const yesterday = translate('84725', [], isChinese ? '昨天' : 'Yesterday')

    return `${yesterday} ${targetTime.format('HH:mm')}`
  }
  if (dayDiff === 2) return translate('84726', [2], isChinese ? '2天前' : 'Two days ago')
  if (dayDiff === 3) return translate('84726', [3], isChinese ? '3天前' : 'Three days ago')
  if (dayDiff >= 4 && dayDiff <= 7) return translate('84727', [1], isChinese ? '1周内' : 'In a Week')
  if (now.year() === targetTime.year()) return targetTime.format('MM-DD')
  return targetTime.format('YYYY-MM-DD')
}

// 时间戳转换成YYYY-MM-DD格式
export const formatTimestamp = (timestamp: AppTimeInput) => {
  const normalizedTimestamp = normalizeTimestamp(timestamp)
  if (!normalizedTimestamp) return ''
  return dayjs(normalizedTimestamp).format('YYYY-MM-DD')
}
